/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import { Box, Typography, alpha, keyframes } from "@mui/material";
import pearlLogo from "assets/images/pearl-logo.png";
import colors from "assets/theme/base/colors";
import { fullHeightSx } from "assets/theme/base/appScale";

const pearlPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 4px 12px rgba(21, 101, 192, 0.18));
  }
  50% {
    transform: scale(1.06);
    filter: drop-shadow(0 8px 20px rgba(21, 101, 192, 0.28));
  }
`;

const ringSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.92); }
  50% { opacity: 0.85; transform: scale(1); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const SIZE_MAP = {
  xs: { container: 26, logo: 16, ring: 2, showRing: false, showDots: false },
  sm: { container: 52, logo: 34, ring: 2.5, showRing: true, showDots: false },
  md: { container: 76, logo: 48, ring: 3, showRing: true, showDots: true },
  lg: { container: 96, logo: 62, ring: 3, showRing: true, showDots: true },
  xl: { container: 116, logo: 76, ring: 3.5, showRing: true, showDots: true },
};

const PearlLogoLoader = ({
  message = "Loading...",
  size = "md",
  fullScreen = false,
  overlay = false,
  showMessage = true,
  sx = {},
}) => {
  const dimensions = SIZE_MAP[size] || SIZE_MAP.md;

  const loader = (
    <Box
      role="status"
      aria-live="polite"
      aria-label={message || "Loading"}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: size === "xs" ? 0 : 1.5,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: dimensions.container,
          height: dimensions.container,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {dimensions.showRing && (
          <>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.primary.main, 0.14)} 0%, transparent 72%)`,
                animation: `${glowPulse} 2.4s ease-in-out infinite`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `${dimensions.ring}px solid ${alpha(colors.primary.main, 0.12)}`,
                borderTopColor: colors.primary.main,
                borderRightColor: alpha(colors.warning.main, 0.75),
                animation: `${ringSpin} 1.35s linear infinite`,
              }}
            />
          </>
        )}

        <Box
          component="img"
          src={pearlLogo}
          alt=""
          aria-hidden
          sx={{
            width: dimensions.logo,
            height: "auto",
            maxHeight: dimensions.logo,
            objectFit: "contain",
            position: "relative",
            zIndex: 1,
            animation: `${pearlPulse} 2s ease-in-out infinite`,
          }}
        />
      </Box>

      {showMessage && message && size !== "xs" && (
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: size === "sm" ? "0.82rem" : "0.92rem",
              fontWeight: 600,
              color: colors.text.main,
              letterSpacing: "0.01em",
            }}
          >
            {message}
          </Typography>

          {dimensions.showDots && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.6, mt: 1 }}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: colors.primary.main,
                    animation: `${dotBounce} 1.1s ease-in-out infinite`,
                    animationDelay: `${index * 0.16}s`,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  if (overlay) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(colors.background.default, 0.82),
          backdropFilter: "blur(8px)",
        }}
      >
        {loader}
      </Box>
    );
  }

  if (fullScreen) {
    return (
      <Box
        sx={{
          ...fullHeightSx,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.background.default,
        }}
      >
        {loader}
      </Box>
    );
  }

  return loader;
};

PearlLogoLoader.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  fullScreen: PropTypes.bool,
  overlay: PropTypes.bool,
  showMessage: PropTypes.bool,
  sx: PropTypes.object,
};

export default PearlLogoLoader;
